import { useLocation } from "react-router-dom";
import { usePartnerBySlugQuery } from "~/store/api";

export interface PartnerMeta {
  name: string;
  email: string;
  freeMembership: boolean;
  logoUrl: string | null;
}

interface UsePartner {
  isPartner: boolean;
  partnerSlug: string;
  partnerSlugify: (route: string) => string;
  partnerMeta: PartnerMeta;
  qrCodeKey: string;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

// The opaque QR carry key arrives as a `?qr=` query param at the partner
// activate URL. The sign-in wall drops query params on redirect, so the path
// is not a reliable survival mechanism — we stash the key in sessionStorage on
// first load and read it back during signup. usePartner is the natural seam
// because it already derives partner context from the URL on every render.
export const QR_CODE_KEY_STORAGE_KEY = "qr_code_key";
// We also record the partner slug the key was captured under. The server-side
// cross-tenant guard bounds the blast radius, but a stale key from partner A's
// flow must not ride into partner B's flow in the same tab — so we drop the
// stash when the active partner slug no longer matches the captured context.
export const QR_CODE_PARTNER_STORAGE_KEY = "qr_code_partner_slug";

// sessionStorage access can throw in restricted browser contexts (Safari
// private mode, storage disabled by policy). QR carry-through is an optional
// convenience layered on top of manual activation, so every access is wrapped:
// a thrown read degrades to "" and the signup flow continues uninterrupted.
const readQrStorage = (key: string): string => {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeQrStorage = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Stash is best-effort; failing to persist just disables carry-through.
  }
};

const removeQrStorage = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
};

export const clearQrCodeKey = (): void => {
  removeQrStorage(QR_CODE_KEY_STORAGE_KEY);
  removeQrStorage(QR_CODE_PARTNER_STORAGE_KEY);
};

const resolveQrCodeKey = (search: string, partnerSlug: string): string => {
  const fromQuery = new URLSearchParams(search || "").get("qr");

  if (fromQuery) {
    writeQrStorage(QR_CODE_KEY_STORAGE_KEY, fromQuery);
    // Scope the stash to the partner it was captured under (empty on a
    // non-partner route, which still keys distinctly against any partner slug).
    writeQrStorage(QR_CODE_PARTNER_STORAGE_KEY, partnerSlug);
    return fromQuery;
  }

  const stashedKey = readQrStorage(QR_CODE_KEY_STORAGE_KEY);
  if (!stashedKey) return "";

  // A stashed key only carries through within its originating partner context.
  // Once we're on a *different* partner's route, the stale key is dropped so it
  // cannot leak into the new partner's signup. A non-partner route (empty slug,
  // e.g. the post-redirect /onboarding path) preserves the stash so the key
  // survives the sign-in wall as intended.
  const stashedPartner = readQrStorage(QR_CODE_PARTNER_STORAGE_KEY);
  if (partnerSlug && partnerSlug !== stashedPartner) {
    clearQrCodeKey();
    return "";
  }

  return stashedKey;
};

const EMPTY_META: PartnerMeta = {
  name: "",
  email: "",
  freeMembership: true,
  logoUrl: null,
};

export const usePartner = (): UsePartner => {
  const location = useLocation();
  const partnerSlug =
    location.pathname.split("/")[1] === "partner"
      ? location.pathname.split("/")[2] || ""
      : "";

  const qrCodeKey = resolveQrCodeKey(location.search, partnerSlug);

  const { currentData, isFetching, isSuccess, isError, error, refetch } =
    usePartnerBySlugQuery(partnerSlug, { skip: !partnerSlug });

  // Use currentData (not data): data retains the previous slug's result while a
  // new slug is fetching, which would briefly show the wrong partner.
  const attributes = currentData?.data?.attributes;
  const isPartner = Boolean(isSuccess && attributes);

  // Pending while a request is in flight with no current-arg data yet — covers
  // both initial load and slug changes, so the UI stays on the loader instead
  // of flashing the previous slug's partner.
  const isPending = isFetching && !currentData;

  // A 404 is a definitive "not a partner" (handled by redirect). Network/5xx are
  // transient and must NOT be treated as not-a-partner — see PartnerRedirector.
  const status =
    isError && error && typeof error === "object" && "status" in error
      ? (error as { status?: number | string }).status
      : undefined;
  const isTransientError = Boolean(isError && status !== 404);

  const partnerMeta: PartnerMeta = attributes
    ? {
        name: attributes.name,
        email: attributes.email,
        freeMembership: attributes.freeMembership,
        logoUrl: attributes.logoUrl,
      }
    : EMPTY_META;

  return {
    isPartner,
    partnerSlug,
    partnerSlugify: partnerSlugify(partnerSlug),
    partnerMeta,
    qrCodeKey,
    isLoading: isPending,
    isError: isTransientError,
    refetch,
  };
};

const partnerSlugify = (partnerSlug: string) => (route: string) => {
  if (!partnerSlug) return route;

  const partnerRoute = route.replace(":slug", partnerSlug);

  return partnerRoute;
};
